import { Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { Action } from '@/lib/types';
import { useAtomValue } from 'jotai';
import { campaignDataAtom } from '@/store';
import { useCampaignAllStateApproveMutationOptions, useCampaignStateMutationOptions } from '@/lib/query-options';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { CampaignWithTransfer } from '../campaign-with-transfer';

interface PageHeaderProps {
  title: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  googleadsCampaignState?: string;
  googleAdsSyncStatus?: string;
  actions?: Action[];
  showMoreActions?: boolean;
  showTabBar?: boolean;
  currentTab?: string;
  onTabChange?: (tab: string) => void;
  onCopy?: () => void;
  onDelete?: () => void;
}

export function PageHeader({
  title,
  icon: Icon,
  actions,
  showMoreActions,
  showTabBar,
  currentTab,
  onTabChange,
  onCopy,
  onDelete,
}: PageHeaderProps) {
  const campaign = useAtomValue(campaignDataAtom);
  const googleCampaignUpdateMutation = useCampaignStateMutationOptions(campaign?.id ?? 0);



  const campaignAllStateApproveMutation = useCampaignAllStateApproveMutationOptions(campaign?.slug ?? '');
  useLoadingOverlay(googleCampaignUpdateMutation.isPending || campaignAllStateApproveMutation.isPending);



  return (
    <div className='flex items-center justify-between py-4 border-b border-gray-200 h-[72px]'>
      <div className='flex items-center gap-6 min-w-0 flex-1'>
        <div className='flex items-center gap-3 min-w-0'>
          {(() => {
            const IconComponent = Icon || Megaphone;
            return <IconComponent className='h-7 w-7 text-[#0f172a] flex-shrink-0' />;
          })()}
          <h1 className='text-2xl font-semibold text-[#0f172a] truncate'>{title}</h1>
        </div>
      </div>
      <div className='flex items-center'>
        {showTabBar && (
          <Tabs
            value={currentTab}
            className={window.location.pathname.startsWith('/organizations') ? "w-80" : "w-full"}
            onValueChange={onTabChange}
          >
            {window.location.pathname.startsWith('/organizations') ? (
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="members">Members</TabsTrigger>
                <TabsTrigger value="providers">Providers</TabsTrigger>
                <TabsTrigger value="billing">Billing</TabsTrigger>
              </TabsList>
            ) : (
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="ads">Ads</TabsTrigger>
                <TabsTrigger value="assets">Assets</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
            )}
          </Tabs>
        )}
        <div className='flex justify-between items-center gap-3'>
          {/* Apply all changes modal removed */}
        </div>

        {actions?.map(({ label, onClick, icon: ActionIcon }, index) => (
          <Button
            key={index}
            onClick={onClick}
            variant="outline"
            size="lg"
            className="px-4 py-2 inline-flex items-center space-x-1 font-medium h-9"
          >
            {ActionIcon ? (
              <>
                <ActionIcon className="h-4 w-4" />
                <span>{label}</span>
              </>
            ) : (
              <span>+ {label}</span>
            )}
          </Button>
        ))}
        {showMoreActions && window.location.pathname.includes('/campaigns/') && campaign && (
          <CampaignWithTransfer
            campaign={campaign}
            variant='header'
            onCopy={onCopy}
            onDelete={onDelete}
          />
        )}
      </div>
    </div>
  );
}
